import jsPDF from "jspdf";
import { fileUrl } from "./api";
import { getCompanySettings, companyContactLines } from "./companySettings";
import { logError } from "./logger";

async function loadLogoDataURL(company) {
  if (!company?.logo_path) return null;
  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          canvas.getContext("2d").drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/jpeg", 0.8));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = fileUrl(company.logo_path);
    });
  } catch {
    return null;
  }
}

function fmtBRL(v) {
  return `US$ ${Number(v || 0).toFixed(2)}`;
}
function fmtDate(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-US");
  } catch {
    return d;
  }
}

function header(doc, title, subtitle, period, company, logoData) {
  const W = doc.internal.pageSize.getWidth();
  doc.setFillColor(37, 99, 235);
  doc.rect(0, 0, W, 22, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  let nameX = 15;
  if (logoData) {
    try {
      doc.addImage(logoData, "JPEG", 15, 4, 14, 14);
      nameX = 33;
    } catch (e) {
      logError("addImage logo failed:", e);
    }
  }
  doc.text(company?.name || "Clean Manager USA", nameX, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("Relatório Financeiro", W - 15, 14, { align: "right" });

  // Contact strip below header band
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8);
  const contact = company ? companyContactLines(company).join(" · ") : "";
  if (contact) doc.text(contact, 15, 28);

  doc.setTextColor(15, 23, 42);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(title, 15, contact ? 40 : 34);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139);
  const baseY = contact ? 46 : 40;
  if (subtitle) doc.text(subtitle, 15, baseY);
  if (period) doc.text(`Período: ${period}`, 15, baseY + 5);
  return baseY + 12;
}

function footer(doc, company) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  doc.setDrawColor(226, 232, 240);
  doc.line(15, H - 12, W - 15, H - 12);
  doc.setTextColor(148, 163, 184);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  const brand = company?.name || "Clean Manager USA";
  doc.text(`Gerado em ${new Date().toLocaleString("en-US")} · ${brand}`, 15, H - 8);
}

function tableRow(doc, y, cols, widths, opts = {}) {
  const W = doc.internal.pageSize.getWidth();
  const startX = 15;
  if (opts.head) {
    doc.setFillColor(241, 245, 249);
    doc.rect(startX, y - 5, W - 30, 8, "F");
    doc.setFont("helvetica", "bold");
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(8);
  } else {
    doc.setFont("helvetica", "normal");
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(9);
  }
  let x = startX + 2;
  cols.forEach((c, i) => {
    const txt = String(c ?? "—");
    const w = widths[i];
    const align = opts.align?.[i] || "left";
    if (align === "right") {
      doc.text(txt, x + w - 3, y, { align: "right" });
    } else {
      doc.text(txt.slice(0, 60), x, y);
    }
    x += w;
  });
  if (!opts.head) {
    doc.setDrawColor(241, 245, 249);
    doc.line(startX, y + 2, W - 15, y + 2);
  }
  return y + 6;
}

export async function exportTeamReportPDF(rows, period) {
  const company = await getCompanySettings();
  const logoData = await loadLogoDataURL(company);
  const doc = new jsPDF();
  let y = header(doc, "Por Equipe", `${rows.length} equipes com limpezas finalizadas`, period, company, logoData);
  const total = rows.reduce((s, r) => s + (r.revenue || 0), 0);
  const totalCount = rows.reduce((s, r) => s + (r.count || 0), 0);
  doc.setFillColor(239, 246, 255);
  doc.setDrawColor(191, 219, 254);
  doc.roundedRect(15, y, 180, 14, 2, 2, "FD");
  doc.setTextColor(30, 64, 175);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`TOTAL: ${totalCount} limpezas · ${fmtBRL(total)}`, 18, y + 9);
  y += 22;

  y = tableRow(doc, y, ["Equipe", "Limpezas", "Faturado"], [110, 30, 40], {
    head: true,
    align: ["left", "right", "right"],
  });
  rows.forEach((r) => {
    if (y > 280) {
      footer(doc, company);
      doc.addPage();
      y = 20;
    }
    y = tableRow(
      doc,
      y,
      [r.team_name, r.count, fmtBRL(r.revenue)],
      [110, 30, 40],
      { align: ["left", "right", "right"] },
    );
  });
  footer(doc, company);
  doc.save(`relatorio-equipes-${Date.now()}.pdf`);
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  } catch {
    return "—";
  }
}

function plural(n, singular, plural) {
  return `${n} ${n === 1 ? singular : plural}`;
}

function ensureSpace(doc, y, need = 20, company) {
  if (y + need > 285) {
    footer(doc, company);
    doc.addPage();
    return 20;
  }
  return y;
}

export async function exportSingleCollaboratorReceiptPDF(row, period) {
  const company = await getCompanySettings();
  const logoData = await loadLogoDataURL(company);
  const doc = new jsPDF();
  let y = header(
    doc,
    "Recibo de Pagamento",
    `${row.name || "—"} · pagamento por dia`,
    period,
    company,
    logoData,
  );

  // Bloco resumo destacado
  doc.setFillColor(220, 252, 231);
  doc.setDrawColor(134, 239, 172);
  doc.roundedRect(15, y, 180, 26, 3, 3, "FD");
  doc.setTextColor(20, 83, 45);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("VALOR A RECEBER", 18, y + 8);
  doc.setFontSize(20);
  doc.text(fmtBRL(row.to_receive || 0), 18, y + 19);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(22, 101, 52);
  doc.text(
    `Diária ${fmtBRL(row.valor_por_dia || 0)} · ${plural(row.days_worked || 0, "dia trabalhado", "dias trabalhados")} · ${plural(row.count || 0, "casa", "casas")}`,
    192,
    y + 14,
    { align: "right" },
  );
  y += 34;

  // Subtabela detalhada
  y = tableRow(
    doc,
    y,
    ["Cliente", "Data", "Início", "Fim", "Total do dia"],
    [70, 30, 25, 25, 30],
    { head: true, align: ["left", "left", "right", "right", "right"] },
  );

  (row.days_list || []).forEach((d) => {
    (d.jobs || []).forEach((j, idx) => {
      y = ensureSpace(doc, y, 8, company);
      const isLast = idx === d.jobs.length - 1;
      y = tableRow(
        doc,
        y,
        [
          j.client_name || "—",
          fmtDate(j.date),
          fmtTime(j.start_time),
          fmtTime(j.end_time),
          isLast ? fmtBRL(d.day_total || 0) : "—",
        ],
        [70, 30, 25, 25, 30],
        { align: ["left", "left", "right", "right", "right"] },
      );
    });
  });

  footer(doc, company);
  const safeNameRecibo = (row.name || "colaborador").replace(/[^a-zA-Z0-9-]/g, "_");
  doc.save(`recibo-${safeNameRecibo}-${Date.now()}.pdf`);
}

/**
 * Invoice PDF (Fatura para o cliente) — replica o layout do recibo do
 * colaborador mas com tom de cobrança/B2C: header amarelo/âmbar, total
 * destacado, lista de limpezas finalizadas no período + instruções de
 * pagamento. Reusa header/footer/tableRow utilitários.
 */
export async function exportClientInvoicePDF(row, period) {
  const company = await getCompanySettings();
  const logoData = await loadLogoDataURL(company);
  const doc = new jsPDF();
  let y = header(
    doc,
    "Fatura (Invoice)",
    `${row.name || "—"} · ${period || "Período"}`,
    period,
    company,
    logoData,
  );

  // Box destacado
  doc.setFillColor(254, 249, 195);
  doc.setDrawColor(253, 224, 71);
  doc.roundedRect(15, y, 180, 26, 3, 3, "FD");
  doc.setTextColor(133, 77, 14);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("VALOR TOTAL", 18, y + 8);
  doc.setFontSize(20);
  doc.text(fmtBRL(row.total_paid || 0), 18, y + 19);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(146, 64, 14);
  doc.text(
    `${plural(row.count || 0, "limpeza realizada", "limpezas realizadas")} · ${row.frequency || "—"}`,
    192,
    y + 14,
    { align: "right" },
  );
  y += 34;

  // Endereço do cliente
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text("Local do serviço", 12, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  const addr = [row.address, row.city, row.state].filter(Boolean).join(", ");
  doc.text(addr || "—", 12, y);
  y += 8;

  // Subtabela de limpezas
  y = tableRow(
    doc,
    y,
    ["Data", "Início", "Fim", "Equipe", "Valor"],
    [30, 25, 25, 70, 30],
    { head: true, align: ["left", "right", "right", "left", "right"] },
  );
  (row.jobs || []).forEach((j) => {
    y = ensureSpace(doc, y, 8, company);
    y = tableRow(
      doc,
      y,
      [
        fmtDate(j.date),
        fmtTime(j.start_time),
        fmtTime(j.end_time),
        j.team_name || "—",
        fmtBRL(j.value || 0),
      ],
      [30, 25, 25, 70, 30],
      { align: ["left", "right", "right", "left", "right"] },
    );
  });
  y += 6;

  // Footer com instruções de pagamento
  y = ensureSpace(doc, y, 30, company);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30, 64, 175);
  doc.text("Instruções de pagamento", 12, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(15, 23, 42);
  ["Por favor efetue o pagamento até a data de vencimento.",
   "Métodos aceitos: Zelle, Venmo, Cash.",
   "Em caso de dúvidas, entre em contato pelo WhatsApp."].forEach((line) => {
    doc.text(`• ${line}`, 14, y);
    y += 4.5;
  });

  footer(doc, company);
  const safeName = (row.name || "cliente").replace(/[^a-zA-Z0-9-]/g, "_");
  doc.save(`invoice-${safeName}-${Date.now()}.pdf`);
}


export async function exportCollaboratorReportPDF(rows, period) {
  const company = await getCompanySettings();
  const logoData = await loadLogoDataURL(company);
  const doc = new jsPDF();
  let y = header(doc, "Por Colaborador", `${rows.length} colaboradores · pagamento por dia`, period, company, logoData);
  const total = rows.reduce((s, r) => s + (r.to_receive || 0), 0);
  const totalDays = rows.reduce((s, r) => s + (r.days_worked || 0), 0);
  const totalHouses = rows.reduce((s, r) => s + (r.count || 0), 0);
  doc.setFillColor(240, 253, 244);
  doc.setDrawColor(187, 247, 208);
  doc.roundedRect(15, y, 180, 14, 2, 2, "FD");
  doc.setTextColor(20, 83, 45);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`TOTAL A PAGAR: ${fmtBRL(total)} · ${totalDays} dias · ${totalHouses} casas`, 18, y + 9);
  y += 22;

  rows.forEach((r) => {
    y = ensureSpace(doc, y, 38, company);
    // Card header
    doc.setFillColor(241, 245, 249);
    doc.setDrawColor(203, 213, 225);
    doc.roundedRect(15, y, 180, 14, 2, 2, "FD");
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(r.name || "—", 18, y + 6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(
      `Diária ${fmtBRL(r.valor_por_dia || 0)} · ${plural(r.days_worked || 0, "dia", "dias")} · ${plural(r.count || 0, "casa", "casas")}`,
      18,
      y + 11,
    );
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(20, 83, 45);
    doc.text(`A RECEBER: ${fmtBRL(r.to_receive || 0)}`, 192, y + 8, { align: "right" });
    y += 18;

    // Header da subtabela
    y = tableRow(
      doc,
      y,
      ["Cliente", "Data", "Início", "Fim", "Total do dia"],
      [70, 30, 25, 25, 30],
      { head: true, align: ["left", "left", "right", "right", "right"] },
    );

    // Linhas (agrupadas por dia)
    (r.days_list || []).forEach((d) => {
      (d.jobs || []).forEach((j, idx) => {
        y = ensureSpace(doc, y, 8, company);
        const isLast = idx === d.jobs.length - 1;
        y = tableRow(
          doc,
          y,
          [
            j.client_name || "—",
            fmtDate(j.date),
            fmtTime(j.start_time),
            fmtTime(j.end_time),
            isLast ? fmtBRL(d.day_total || 0) : "—",
          ],
          [70, 30, 25, 25, 30],
          { align: ["left", "left", "right", "right", "right"] },
        );
      });
    });
    y += 6;
  });
  footer(doc, company);
  doc.save(`relatorio-colaboradores-${Date.now()}.pdf`);
}

export async function exportClientReportPDF(rows, period) {
  const company = await getCompanySettings();
  const logoData = await loadLogoDataURL(company);
  const doc = new jsPDF();
  let y = header(doc, "Por Cliente", `${rows.length} clientes`, period, company, logoData);
  const total = rows.reduce((s, r) => s + (r.total_paid || 0), 0);
  const totalClean = rows.reduce((s, r) => s + (r.count || 0), 0);
  doc.setFillColor(254, 249, 195);
  doc.setDrawColor(253, 224, 71);
  doc.roundedRect(15, y, 180, 14, 2, 2, "FD");
  doc.setTextColor(133, 77, 14);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.text(`TOTAL RECEBIDO: ${fmtBRL(total)} · ${totalClean} limpezas`, 18, y + 9);
  y += 22;

  rows.forEach((r) => {
    y = ensureSpace(doc, y, 38, company);
    doc.setFillColor(254, 252, 232);
    doc.setDrawColor(254, 240, 138);
    doc.roundedRect(15, y, 180, 14, 2, 2, "FD");
    doc.setTextColor(15, 23, 42);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(r.name || "—", 18, y + 6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    const addr = [r.address, r.city, r.state].filter(Boolean).join(", ");
    doc.text(
      `${addr || "Sem endereço"} · ${r.frequency || "—"} · ${plural(r.count || 0, "limpeza", "limpezas")}`,
      18,
      y + 11,
    );
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(133, 77, 14);
    doc.text(`TOTAL: ${fmtBRL(r.total_paid || 0)}`, 192, y + 8, { align: "right" });
    y += 18;

    y = tableRow(
      doc,
      y,
      ["Data", "Início", "Fim", "Equipe", "Valor"],
      [30, 25, 25, 70, 30],
      { head: true, align: ["left", "right", "right", "left", "right"] },
    );

    (r.jobs || []).forEach((j) => {
      y = ensureSpace(doc, y, 8, company);
      y = tableRow(
        doc,
        y,
        [
          fmtDate(j.date),
          fmtTime(j.start_time),
          fmtTime(j.end_time),
          j.team_name || "—",
          fmtBRL(j.value || 0),
        ],
        [30, 25, 25, 70, 30],
        { align: ["left", "right", "right", "left", "right"] },
      );
    });
    y += 6;
  });
  footer(doc, company);
  doc.save(`relatorio-clientes-${Date.now()}.pdf`);
}

export { fmtBRL, fmtDate };
